import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/Textarea';
import {
  Camera, X, Upload, Trash2, Edit, Save,
  FileText, Download, File, Plus, CheckCircle,
  IdCard, BookOpen, Paperclip, Calendar, Image,
  ArrowRightLeft, Building2, Clock3, ShieldCheck, Archive, UserCheck, KeyRound, Bus
} from 'lucide-react';
import AccesFamilleEleve from './accesFamille/AccesFamilleEleve';
import SectionTransportEleve from './transport/SectionTransportEleve';
import { transport as apiTransport } from './transport/apiTransport';
import { api } from '../services/api';
import { useFeatures } from '../contexts/FeaturesContext';
import StudentCardModal from './StudentCardModal';
import { format, parse, parseISO } from 'date-fns';
import ProtectedComponent from '../components/ProtectedComponent';
import GroupedClassDropdown from './ui/GroupedClassDropdown';
import { MODAL_CONTENT_CLASS, MODAL_OVERLAY_Z40_CLASS } from './ui/modalOverlay';

const DetailModal = ({ eleve, isOpen, onClose, onUpdate, onPhotoChange, onShowToast }) => {
  const { estActive } = useFeatures();
  const interSitesOuvert = estActive('module.intersites');
  // Onglet « Accès famille » : seulement si l'école a ouvert l'espace famille.
  const espaceFamilleOuvert = estActive('module.parents');
  // Sans comptabilite, changer de classe n'a pas d'effet financier : le serveur
  // l'applique immediatement au lieu de le soumettre au Centre de Support.
  // L'ecran doit dire la meme chose que ce qu'il fait.
  const comptabiliteOuverte = estActive('module.comptabilite');

  // Le changement de classe vivait dans le modal Finance de la liste des eleves,
  // avec l'etat des paiements et les frais d'inscription. C'est un acte de
  // scolarite, pas un acte comptable : sa place est ici, dans le dossier.
  const [classesDisponibles, setClassesDisponibles] = useState([]);
  const [sectionsDisponibles, setSectionsDisponibles] = useState([]);
  const [changementClasseOuvert, setChangementClasseOuvert] = useState(false);
  const [classeCible, setClasseCible] = useState('');
  const [changementEnCours, setChangementEnCours] = useState(false);
  const [motifChangement, setMotifChangement] = useState('');
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [localEleve, setLocalEleve] = useState({ ...eleve });
  // Le CHEMIN RELATIF rendu par l'API, et non `https://www.klasys.com${...}`.
  //
  // POURQUOI. L'adresse absolue demandait la photo au domaine de www, quelle que
  // soit l'ecole de la page. Cela ne "marchait" que parce que uploads/photos
  // etait un dossier COMMUN a toutes les ecoles, servi en statique sans jeton.
  // Les photos sont desormais rangees par ecole et l'adresse rendue par l'API
  // porte une signature liee A CETTE ECOLE : depuis csap1, une adresse signee
  // pour csap1 envoyee au domaine de www serait verifiee contre « www »,
  // ne concorderait pas, et la fiche eleve n'afficherait plus aucune photo.
  // Les cinq autres ecrans qui affichent une photo d'eleve font deja ainsi.
  const [photoPreview, setPhotoPreview] = useState(eleve.photo_url || null);
  const [isUploading, setIsUploading] = useState(false);
  const [isGeneratingCard, setIsGeneratingCard] = useState(false);
  const [error, setError] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [isCapturingPhoto, setIsCapturingPhoto] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  // L'onglet du bus n'existe que si le registre du transport est installé (lot 3a).
  const [busInstalle, setBusInstalle] = useState(false);
  useEffect(() => {
    let actif = true;
    apiTransport.reglages()
      .then((r) => { if (actif) setBusInstalle(Boolean(r?.installe)); })
      .catch(() => {});
    return () => { actif = false; };
  }, []);
  const [documents, setDocuments] = useState({
    identite: [],
    diplomes: [],
    autres: []
  });
  const [interSiteTrace, setInterSiteTrace] = useState(null);
  const [isLoadingInterSiteTrace, setIsLoadingInterSiteTrace] = useState(false);
  const fileInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const multipleDocumentInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const chargerClasses = async () => {
    if (classesDisponibles.length > 0) return;
    try {
      const [resultatClasses, resultatSections] = await Promise.all([
        api.classes.getAll(),
        api.sections?.getAll ? api.sections.getAll() : Promise.resolve([])
      ]);
      const listeClasses = Array.isArray(resultatClasses) ? resultatClasses : (resultatClasses?.data || []);
      const listeSections = Array.isArray(resultatSections) ? resultatSections : (resultatSections?.data || []);
      setClassesDisponibles(listeClasses);
      setSectionsDisponibles(listeSections);
    } catch (error) {
      console.error('Erreur chargement classes:', error);
    }
  };

  const confirmerChangementClasse = async () => {
    const cible = Number(classeCible);
    if (!cible || cible === Number(localEleve?.classe_id)) return;

    if (motifChangement.trim().length === 0) {
      onShowToast?.('Le motif du changement de classe est obligatoire', 'error');
      return;
    }

    setChangementEnCours(true);
    try {
      // Le changement passe par le Centre de Support quand il a un effet
      // financier. Sans comptabilite, le serveur l'applique directement et
      // repond `validation_required: false` : la classe a alors reellement
      // change, et l'ecran doit le refleter.
      const resultat = await api.eleves.demanderChangementClasse(localEleve.id, {
        nouvelle_classe_id: cible,
        motif: motifChangement.trim()
      });

      if (!resultat.success) {
        onShowToast?.(resultat.message || "La demande n'a pas pu être envoyée", 'error');
        return;
      }

      setChangementClasseOuvert(false);
      setClasseCible('');
      setMotifChangement('');
      showSuccessToast(
        resultat.message
        || (comptabiliteOuverte ? 'Demande envoyée au Centre de Support' : 'Classe modifiée')
      );

      if (resultat.validation_required === false) {
        onUpdate?.();
      }
    } catch (error) {
      console.error('Erreur demande changement de classe:', error);
      onShowToast?.(error?.message || 'Erreur lors de la demande de changement de classe', 'error');
    } finally {
      setChangementEnCours(false);
    }
  };

  const showSuccessToast = (message) => {
    console.log("Success:", message);
    if (onShowToast) {
      onShowToast(message, 'success');
    }
  };

  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraStream(null);
    setIsCameraActive(false);
    setCameraError(null);
  }, [cameraStream]);

  useEffect(() => {
    if (!isCameraActive || !cameraStream || !videoRef.current) return;

    videoRef.current.srcObject = cameraStream;
    videoRef.current.play().catch(() => {
      setCameraError("Impossible de démarrer l'aperçu caméra.");
    });
  }, [cameraStream, isCameraActive]);

  useEffect(() => {
    if (!isOpen) {
      stopCamera();
    }

    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [isOpen, cameraStream, stopCamera]);


  // À ajouter TEMPORAIREMENT dans ton DetailModal, juste après les imports





  // Dans le useEffect qui charge les données de l'élève
  useEffect(() => {
    console.log("Données brutes reçues de l'API:", eleve);

    const dateValue = eleve.dateNaissance || eleve.date_naissance;
    const formattedDate = formatDateForInput(dateValue);

    setLocalEleve({
      ...eleve,
      dateNaissance: formattedDate,
      inscripteur: eleve.nom_inscripteur || eleve.inscripteur || 'Non renseigné',
      date_inscription: eleve.date_inscription || eleve.created_at || null,
      type_inscription: eleve.type_inscription || eleve.inscription_type || 'new'
    });

    console.log("LocalEleve après initialisation:", {
      nom_inscripteur: eleve.nom_inscripteur,
      inscripteur: eleve.nom_inscripteur
    });

    // Chemin relatif, comme a l'initialisation ci-dessus : c'est l'adresse
    // signee pour l'ecole courante que l'API vient de rendre.
    setPhotoPreview(eleve.photo_url || null);

    // Chargement des documents
    const loadDocuments = async () => {
      try {
        if (eleve.id) {
          const response = await api.eleves.getDocuments(eleve.id);
          if (response) {
            setDocuments({
              identite: response.identite || [],
              diplomes: response.diplomes || [],
              autres: response.autres || []
            });
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des documents:', error);
        setError('Erreur lors du chargement des documents');
      }
    };

    const loadInterSiteTrace = async () => {
      // Un eleve transfere ne peut pas exister dans une ecole sans inter-sites,
      // sauf si elle en a eu le module puis l a perdu. Sans ce garde-fou,
      // l appel partirait quand meme et reviendrait en 403.
      const shouldLoadTrace = interSitesOuvert && (eleve.type_inscription === 'transfert'
        || Boolean(eleve.intersite_uuid)
        || Boolean(eleve.provient_de_site)
        || Boolean(eleve.date_arrivee_transfert));

      if (!eleve.id || !shouldLoadTrace) {
        setInterSiteTrace(null);
        return;
      }

      setIsLoadingInterSiteTrace(true);
      try {
        const response = await api.eleves.getInterSiteTransferTrace(eleve.id);
        setInterSiteTrace(response?.is_transferred ? response : null);
      } catch (error) {
        console.error('Erreur lors du chargement de la tracabilite inter-site:', error);
        setInterSiteTrace(null);
      } finally {
        setIsLoadingInterSiteTrace(false);
      }
    };

    if (isOpen && eleve.id) {
      loadDocuments();
      loadInterSiteTrace();
    }
  }, [eleve, isOpen]);




  // Fonction de formatage pour la date d'inscription
  const formatInscriptionDate = (dateString) => {
    if (!dateString || dateString === 'Non renseigné') return 'Non renseigné';

    try {
      // Si c'est déjà au format souhaité, le retourner tel quel
      if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
        return dateString;
      }

      // Sinon, convertir la date au format souhaité
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Si conversion impossible

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch (e) {
      console.error("Erreur lors du formatage de la date d'inscription:", e);
      return dateString;
    }
  };



  // Fonction pour formater correctement la date sans problèmes de fuseau horaire

  // Fonction améliorée pour formater correctement la date
  const formatDate = (dateString) => {
    if (!dateString) return 'Non renseigné';

    try {
      // Si la date est au format YYYY-MM-DD
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        // Utiliser parseISO qui est plus fiable pour les dates ISO
        const parsedDate = parseISO(dateString);
        // Formater en DD/MM/YYYY
        return format(parsedDate, 'dd/MM/yyyy');
      }

      // Pour les dates avec timestamp (2025-03-13T23:00:00.000Z)
      if (typeof dateString === 'string' && dateString.includes('T')) {
        const parsedDate = parseISO(dateString);
        return format(parsedDate, 'dd/MM/yyyy');
      }

      // Essayer de parser d'autres formats
      const parsedDate = new Date(dateString);
      if (!isNaN(parsedDate.getTime())) {
        return format(parsedDate, 'dd/MM/yyyy');
      }
    } catch (e) {
      console.error("Erreur lors du formatage de la date:", e);
    }

    return 'Format de date invalide';
  };




  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('parent.')) {
      const parentField = name.split('.')[1];
      setLocalEleve(prev => ({
        ...prev,
        parent: {
          ...prev.parent,
          [parentField]: value
        }
      }));
    } else if (name === 'dateNaissance') {
      // Pour la date de naissance, on utilise exactement la valeur fournie par le champ date
      // sans aucune conversion JavaScript qui pourrait modifier le format
      console.log("Date brute saisie:", value); // Pour débogage
      setLocalEleve(prev => ({
        ...prev,
        dateNaissance: value,

      }));
    } else if (name === 'sexe') {
      // Pour le sexe, s'assurer que la valeur est soit 'M', 'F', ou '' (et jamais null ou undefined)
      console.log("Sexe saisi:", value); // Pour débogage
      setLocalEleve(prev => ({
        ...prev,
        sexe: value
      }));
    } else {
      setLocalEleve(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Fonction améliorée pour formater la date pour le champ input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    try {
      // Si c'est déjà au format YYYY-MM-DD, le retourner tel quel
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }

      // Pour les dates avec timestamp (2025-03-13T23:00:00.000Z)
      if (typeof dateString === 'string' && dateString.includes('T')) {
        const parsedDate = parseISO(dateString);
        return format(parsedDate, 'yyyy-MM-dd');
      }

      // Pour d'autres formats
      const parsedDate = new Date(dateString);
      if (!isNaN(parsedDate.getTime())) {
        return format(parsedDate, 'yyyy-MM-dd');
      }
    } catch (e) {
      console.error("Erreur lors du formatage de la date pour le champ input:", e);
    }

    return '';
  };

  const buildDisplayEleve = (source, fallback = {}) => {
    const dateValue = source.dateNaissance || source.date_naissance;

    return {
      ...source,
      dateNaissance: formatDateForInput(dateValue),
      inscripteur: source.nom_inscripteur || source.inscripteur || fallback.inscripteur || 'Non renseigné',
      date_inscription: source.date_inscription || source.created_at || fallback.date_inscription || null,
      type_inscription: source.type_inscription || source.inscription_type || fallback.type_inscription || 'new'
    };
  };

  const getAdmissionTypeLabel = (type) => {
    if (type === 'new' || type === 'Inscription') return 'Inscription';
    if (type === 'renewal' || type === 'Réinscription' || type === 'Reinscription') return 'Réinscription';
    if (type === 'transfert' || type === 'Transfert') return 'Transfert';
    return 'Non renseigné';
  };

  const hasInterSiteSignal = Boolean(
    localEleve.type_inscription === 'transfert'
    || localEleve.intersite_uuid
    || localEleve.provient_de_site
    || localEleve.date_arrivee_transfert
  );

  const traceDate = (value) => formatInscriptionDate(value) || 'Non renseignÃ©';
  const traceValue = (value) => value || 'Non renseignÃ©';
  const traceSite = (value) => value ? String(value).toUpperCase() : 'Site non renseignÃ©';

  const archiveTotal = interSiteTrace?.archive?.resume
    ? Object.entries(interSiteTrace.archive.resume).reduce((total, [key, value]) => {
      if (key === 'mois_payes') return total + Number(value || 0);
      return total + Number(value?.total || 0);
    }, 0)
    : 0;

  const archiveResume = interSiteTrace?.archive?.resume || {};
  const archiveDetails = [
    {
      label: 'Paiements',
      count: archiveResume.paiements?.total || 0,
      description: 'Historique financier, reçus et lignes de paiement du site source.'
    },
    {
      label: 'Notes',
      count: (archiveResume.notes?.total || 0) + (archiveResume.appreciations?.total || 0),
      description: 'Notes, moyennes et appréciations liées au parcours scolaire.'
    },
    {
      label: 'Documents',
      count: archiveResume.documents?.total || 0,
      description: 'Pièces jointes et documents administratifs trouvés dans le dossier source.'
    },
    {
      label: 'Parents',
      count: archiveResume.parents?.total || 0,
      description: 'Anciennes liaisons parent/tuteur et informations associées.'
    },
    {
      label: 'Autres données',
      count: archiveResume.autres?.total || 0,
      description: 'Historiques et autres éléments liés à l’élève avant le transfert.'
    },
    {
      label: 'Mois soldés',
      count: archiveResume.mois_payes || 0,
      description: 'Mois déjà réglés sur le site source et repris en lecture côté finance.'
    }
  ];

  const handleSave = async () => {
    try {
      // Préparer les données au format correct sans manipulation de la date
      const updateData = {
        id: localEleve.id,
        matricule: localEleve.matricule,
        nom: localEleve.nom,
        prenom: localEleve.prenom,
        dateNaissance: localEleve.dateNaissance, // Utiliser directement la chaîne
        lieuNaissance: localEleve.lieuNaissance || localEleve.lieu_naissance,
        nationalite: localEleve.nationalite,
        // ⚠️ NE PAS AJOUTER photo_url A CE QUE LA ROUTE ENREGISTRE.
        // Depuis la signature des adresses de photos, la valeur recue ici porte
        // « ?e=<echeance>&s=<signature> ». PUT /eleves/:id ne l ecrit pas — sa
        // liste de colonnes ne la contient pas — donc ce champ est ignore, et
        // c est tant mieux : stocke tel quel, il figerait une echeance de 24
        // heures dans la base, et l intergiciel refuse de resigner un chemin qui
        // porte deja un « ? ». La photo casserait le lendemain, definitivement.
        photo_url: localEleve.photo_url,
        sexe: localEleve.sexe,
        parent: {
          nom_prenom: localEleve.parent?.nom_prenom,
          email: localEleve.parent?.email,
          telephone: localEleve.parent?.telephone,
          adresse: localEleve.parent?.adresse
        }
      };

      console.log("Données avant envoi API:", updateData);
      console.log("Date de naissance:", updateData.dateNaissance);

      await onUpdate(updateData);
      setIsEditing(false);
      showSuccessToast('Informations mises à jour avec succès');
    } catch (error) {
      console.error("Erreur lors de l'enregistrement:", error);
      setError("Erreur lors de l'enregistrement des modifications");
    }
  };



  const handleCancel = () => {
    stopCamera();
    setLocalEleve(buildDisplayEleve(eleve, localEleve));
    setIsEditing(false);
  };

  const handleSelectPhoto = () => {
    stopCamera();
    fileInputRef.current?.click();
  };

  const handleStartCamera = async () => {
    setError(null);
    setCameraError(null);

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("La prise de photo n'est pas disponible sur ce navigateur.");
      return;
    }

    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: 'user',
          width: { ideal: 900 },
          height: { ideal: 900 }
        }
      });

      setCameraStream(stream);
      setIsCameraActive(true);
    } catch (error) {
      console.error("Erreur lors de l'ouverture de la caméra:", error);
      setCameraError("Impossible d'ouvrir la caméra. Vérifiez l'autorisation du navigateur.");
    }
  };

  const handleCapturePhoto = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
      setCameraError("La caméra n'est pas encore prête. Réessayez dans un instant.");
      return;
    }

    setIsCapturingPhoto(true);
    setCameraError(null);

    try {
      const size = Math.min(video.videoWidth, video.videoHeight);
      const sx = (video.videoWidth - size) / 2;
      const sy = (video.videoHeight - size) / 2;
      canvas.width = 720;
      canvas.height = 720;

      const context = canvas.getContext('2d');
      context.drawImage(video, sx, sy, size, size, 0, 0, canvas.width, canvas.height);

      const previewDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      setPhotoPreview(previewDataUrl);

      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.9);
      });

      if (!blob) {
        throw new Error('Capture photo impossible');
      }

      const filenameBase = localEleve.matricule || `${localEleve.nom || 'eleve'}-${localEleve.prenom || 'photo'}`;
      const file = new window.File([blob], `${filenameBase}-photo.jpg`, { type: 'image/jpeg' });

      await uploadPhoto(file);
      stopCamera();
    } catch (error) {
      console.error('Erreur lors de la capture photo:', error);
      setCameraError("Impossible d'enregistrer la photo capturée.");
    } finally {
      setIsCapturingPhoto(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setError('Format de fichier non pris en charge. Utilisez JPG ou PNG.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB
      setError('La taille de l\'image ne doit pas dépasser 5 MB');
      return;
    }

    // Créer un aperçu
    const reader = new FileReader();
    reader.onload = (event) => {
      setPhotoPreview(event.target.result);
    };
    reader.readAsDataURL(file);

    // Uploader la photo
    uploadPhoto(file);
  };

  const uploadPhoto = async (file) => {
    setIsUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('photo', file);

    try {
      const response = await api.eleves.uploadPhoto(eleve.id, formData);

      const photoUrl = response.photo_url;
      const updatedEleve = {
        ...localEleve,
        photo_url: photoUrl
      };
      setLocalEleve(updatedEleve);
      // La reponse de l'envoi porte deja le chemin signe pour cette ecole : on
      // l'affiche tel quel, sans rebatir d'adresse. C'est par cette duplication
      // que l'ecart s'etait installe.
      setPhotoPreview(photoUrl || null);
      onPhotoChange?.(updatedEleve);

      showSuccessToast('Photo ajoutée avec succès');
      return response;
    } catch (error) {
      console.error('Erreur lors de l\'upload de la photo:', error);
      setError('Erreur lors de l\'upload de la photo');
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeletePhoto = async () => {
    try {
      await api.eleves.deletePhoto(eleve.id);
      setPhotoPreview(null);

      const updatedEleve = {
        ...localEleve,
        photo_url: null
      };
      setLocalEleve(updatedEleve);
      onPhotoChange?.(updatedEleve);

      showSuccessToast('Photo supprimée avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression de la photo:', error);
      setError('Erreur lors de la suppression de la photo');
    }
  };

  const handleMultipleDocumentUpload = async (e, type) => {
    if (!e.target.files || e.target.files.length === 0) return;

    setIsUploading(true);
    setError(null);

    const formData = new FormData();

    // Ajouter chaque fichier au FormData
    for (let i = 0; i < e.target.files.length; i++) {
      const file = e.target.files[i];
      formData.append(type === 'identite' ? 'document' : 'documents', file);
    }

    try {
      let response;

      if (type === 'identite') {
        response = await api.eleves.uploadIdentityDoc(eleve.id, formData);
      } else if (type === 'diplomes') {
        response = await api.eleves.uploadDiplomas(eleve.id, formData);
      } else if (type === 'autres') {
        response = await api.eleves.uploadOtherDocs(eleve.id, formData);
      }

      if (response && response.success) {
        // Recharger les documents
        const updatedDocs = await api.eleves.getDocuments(eleve.id);
        if (updatedDocs) {
          setDocuments({
            identite: updatedDocs.identite || [],
            diplomes: updatedDocs.diplomes || [],
            autres: updatedDocs.autres || []
          });

          showSuccessToast(`Document(s) ajouté(s) avec succès`);
        }
      } else {
        setError(response?.message || 'Erreur lors de l\'upload des documents');
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload des documents:', error);
      setError('Erreur lors de l\'upload des documents');
    } finally {
      setIsUploading(false);
    }
  };





  const handleDeleteDocument = async (documentId) => {
    try {
      const response = await api.eleves.deleteDocument(eleve.id, documentId);

      if (response && !response.error) {
        // Recharger les documents
        const updatedDocs = await api.eleves.getDocuments(eleve.id);
        if (updatedDocs) {
          setDocuments({
            identite: updatedDocs.identite || [],
            diplomes: updatedDocs.diplomes || [],
            autres: updatedDocs.autres || []
          });
        }

        showSuccessToast('Document supprimé avec succès');
      } else {
        setError(response?.message || 'Erreur lors de la suppression du document');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du document:', error);
      setError('Erreur lors de la suppression du document');
    }
  };

  const generateStudentCard = () => {
    // Vérifier si une photo d'identité est disponible
    if (!photoPreview) {
      setError('Une photo didentité est nécessaire pour générer la carte étudiante');
      return;
    }

    // Vérifier si le matricule est disponible
    if (!localEleve.matricule) {
      setError('Un matricule est nécessaire pour générer la carte étudiante');
      return;
    }

    // Ouvrir le modal de carte étudiante
    setIsCardModalOpen(true);
  };


  // Fonction pour obtenir le type MIME d'un document
  const getDocumentIcon = (doc) => {
    if (doc.nom_fichier.endsWith('.pdf') || doc.type === 'application/pdf') {
      return <FileText size={30} className="text-red-500" />;
    } else if (doc.nom_fichier.match(/\.(jpg|jpeg|png|gif)$/i) || doc.type?.startsWith('image/')) {
      return <Image size={30} className="text-blue-500" />;
    } else {
      return <File size={30} className="text-gray-500" />;
    }
  };

  // Fonction pour formater la taille d'un fichier
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className={MODAL_OVERLAY_Z40_CLASS} />


        <Dialog.Content
          className={`fixed top-[55%] left-[50%] transform -translate-x-1/2 -translate-y-1/2 flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl ${MODAL_CONTENT_CLASS}`}
          onInteractOutside={(event) => event.preventDefault()}
        >

          <Tabs.Root value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 flex-1 flex-col">
            {/* En-tête fixe */}
            <div className="sticky top-0 bg-white z-10">
              <div className="p-5 border-b border-gray-200 flex justify-between items-center">
                <Dialog.Title className="text-xl font-bold text-gray-800">
                  Dossier de l'élève
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none transition-colors">
                    <X size={20} className="text-gray-700" />
                  </button>
                </Dialog.Close>
              </div>


              <Tabs.List className="flex bg-gray-50 px-5">
                <Tabs.Trigger
                  value="details"
                  className={`px-4 py-3 font-medium flex items-center gap-2 ${activeTab === 'details'
                      ? 'text-[#246990] border-b-2 border-[#246990]'
                      : 'text-gray-600 hover:text-gray-800'
                    }`}
                >
                  <IdCard size={18} />
                  Informations personnelles
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="documents"
                  className={`px-4 py-3 font-medium flex items-center gap-2 ${activeTab === 'documents'
                      ? 'text-[#246990] border-b-2 border-[#246990]'
                      : 'text-gray-600 hover:text-gray-800'
                    }`}
                >
                  <Paperclip size={18} />
                  Documents administratifs
                </Tabs.Trigger>
                {busInstalle && (
                  <Tabs.Trigger
                    value="transport"
                    className={`px-4 py-3 font-medium flex items-center gap-2 ${activeTab === 'transport'
                        ? 'text-[#246990] border-b-2 border-[#246990]'
                        : 'text-gray-600 hover:text-gray-800'
                      }`}
                  >
                    <Bus size={18} />
                    Bus scolaire
                  </Tabs.Trigger>
                )}
                {espaceFamilleOuvert && (
                  <Tabs.Trigger
                    value="acces-famille"
                    className={`px-4 py-3 font-medium flex items-center gap-2 ${activeTab === 'acces-famille'
                        ? 'text-[#246990] border-b-2 border-[#246990]'
                        : 'text-gray-600 hover:text-gray-800'
                      }`}
                  >
                    <KeyRound size={18} />
                    Accès famille
                  </Tabs.Trigger>
                )}
              </Tabs.List>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">

              {/* Message d'erreur si présent */}
              {error && (
                <div className="m-5 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start gap-3">
                  <div className="text-red-500 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                  </div>
                  <div>
                    {error.split('\n').map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Corps avec Tabs - le défilement est géré par le Dialog.Content */}

              <Tabs.Content value="details">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-5 pb-10">
                  {/* Colonne de gauche - Photo et informations de base */}
                  <div className="space-y-6">

                    {/* Photo de l'élève */}
                    <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-[#246990]/15">
                      <div className="p-4 bg-[#246990]/5 border-b border-[#246990]/10">
                        <h3 className="font-semibold text-[#246990]">Photo d'identité</h3>
                      </div>

                      <div className="p-5 flex flex-col items-center">
                        <div className="relative w-40 h-40 bg-gray-100 rounded-xl overflow-hidden border-2 border-gray-200 mb-4">
                          {photoPreview ? (
                            <img
                              src={photoPreview}
                              alt={`${localEleve.prenom} ${localEleve.nom}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                              <Camera size={40} />
                              <span className="mt-2 text-sm text-center px-4">Aucune photo</span>
                            </div>
                          )}

                          {isUploading && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-white"></div>
                            </div>
                          )}
                        </div>

                        {/* Boutons gestion photo */}
                        <ProtectedComponent module="ELEVES" sousModule="DETAILS" action="PHOTO_MANAGE">
                          <div className="w-full space-y-3">
                            {isEditing && isCameraActive && (
                              <div className="rounded-xl border border-sky-200 bg-sky-50 p-3">
                                <div className="overflow-hidden rounded-lg bg-slate-950 aspect-square">
                                  <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    className="w-full h-full object-cover"
                                  />
                                  <canvas ref={canvasRef} className="hidden" />
                                </div>

                                {cameraError && (
                                  <p className="mt-2 text-xs text-red-600">{cameraError}</p>
                                )}

                                <div className="grid grid-cols-2 gap-2 mt-3">
                                  <Button
                                    type="button"
                                    size="sm"
                                    onClick={handleCapturePhoto}
                                    disabled={isCapturingPhoto || isUploading}
                                    className="bg-[#246990] hover:bg-[#1f5b7d] text-white"
                                  >
                                    <Camera size={15} className="mr-1" />
                                    {isCapturingPhoto || isUploading ? 'Capture...' : 'Capturer'}
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={stopCamera}
                                  >
                                    <X size={15} className="mr-1" />
                                    Fermer
                                  </Button>
                                </div>
                              </div>
                            )}

                            {isEditing ? (
                              <>
                                <div className="grid grid-cols-2 gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleSelectPhoto}
                                    className="flex items-center justify-center gap-2"
                                  >
                                    <Upload size={16} />
                                    Importer
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleStartCamera}
                                    disabled={isCameraActive}
                                    className="flex items-center justify-center gap-2 border-[#246990]/30 text-[#246990] hover:bg-[#246990]/10"
                                  >
                                    <Camera size={16} />
                                    Prendre
                                  </Button>
                                </div>

                                {cameraError && !isCameraActive && (
                                  <p className="text-xs text-red-600">{cameraError}</p>
                                )}

                                {photoPreview && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleDeletePhoto}
                                    className="w-full flex items-center justify-center gap-2 text-red-600 hover:bg-red-50"
                                  >
                                    <Trash2 size={16} />
                                    Supprimer la photo
                                  </Button>
                                )}
                              </>
                            ) : (
                              <div className="text-sm text-gray-500 text-center w-full">
                                {photoPreview ? 'Photo disponible' : 'Aucune photo disponible'}
                              </div>
                            )}


                            <input
                              type="file"
                              ref={fileInputRef}
                              className="hidden"
                              accept="image/jpeg,image/png,image/jpg"
                              onChange={handleFileChange}
                            />
                          </div>
                        </ProtectedComponent>
                      </div>
                    </div>

                    {/* Informations de base */}
                    <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-[#246990]/15">
                      <div className="p-4 bg-[#246990]/5 border-b border-[#246990]/10">
                        <h3 className="font-semibold text-[#246990]">Informations scolaires</h3>
                      </div>

                      <div className="p-5 space-y-4">
                        {/* Matricule */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Matricule</label>
                          <div className="font-medium bg-[#246990]/5 p-2 rounded-lg border border-[#246990]/10 text-gray-800">
                            {localEleve.matricule || 'Non renseigné'}
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <label className="block text-sm font-medium text-gray-700">Classe</label>
                            {/* Avec comptabilite, changer de classe recompose l'attendu :
                                c'est ELEVES.FINANCE.CLASS_CHANGE, le droit prevu pour
                                lui. L'ecran verifiait FINANCE.VIEW, si bien que les
                                enseignants - a qui les ecoles refusent CLASS_CHANGE -
                                pouvaient deplacer un eleve. Sans comptabilite ce n'est
                                qu'une reaffectation : DETAILS.EDIT suffit, exiger un
                                droit financier reserverait un acte de scolarite aux
                                personnes autorisees sur l'argent.
                                La route applique la meme regle : voir
                                `permissionChangementClasse` dans eleves.routes.js. */}
                            <ProtectedComponent
                              module="ELEVES"
                              sousModule={comptabiliteOuverte ? 'FINANCE' : 'DETAILS'}
                              action={comptabiliteOuverte ? 'CLASS_CHANGE' : 'EDIT'}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  const prochain = !changementClasseOuvert;
                                  setChangementClasseOuvert(prochain);
                                  if (prochain) chargerClasses();
                                }}
                                className="text-xs font-semibold text-[#246990] hover:underline"
                              >
                                {changementClasseOuvert ? 'Annuler' : 'Changer de classe'}
                              </button>
                            </ProtectedComponent>
                          </div>
                          <div className="font-medium bg-[#246990]/5 p-2 rounded-lg border border-[#246990]/10 text-gray-800">
                            {localEleve.classe_nom || 'Non renseigné'}
                          </div>

                          {changementClasseOuvert && (
                            <div className="mt-2 rounded-lg border border-[#246990]/20 bg-[#f8fbfd] p-3 space-y-2">
                              <GroupedClassDropdown
                                value={classeCible}
                                classes={classesDisponibles.filter(
                                  (classe) => Number(classe.id) !== Number(localEleve.classe_id)
                                )}
                                sections={sectionsDisponibles}
                                onChange={(id) => setClasseCible(id)}
                                disabled={changementEnCours}
                              />

                              <Input
                                value={motifChangement}
                                onChange={(event) => setMotifChangement(event.target.value)}
                                placeholder="Motif du changement (obligatoire)"
                                disabled={changementEnCours}
                                className="h-9 text-sm"
                              />

                              {/* Le tarif de la nouvelle classe remplace l'ancien sur les
                                  mois non encore réglés, et l'accord de famille est
                                  recomposé s'il y en a un. Le dire ici évite la surprise. */}
                              <p className="text-[11px] leading-snug text-gray-600">
                                {comptabiliteOuverte
                                  ? "Le tarif mensuel de la nouvelle classe s'appliquera à partir du premier mois non entamé. Les mois déjà réglés gardent leur montant, et les frais de début d'année suivent la nouvelle classe s'ils n'ont pas encore été encaissés."
                                  : "L'élève quitte immédiatement sa classe actuelle pour la nouvelle. Ses notes et son historique de scolarité le suivent."}
                              </p>

                              <p className="text-[11px] leading-snug text-[#174866] bg-[#eef7fb] border border-[#246990]/20 rounded-md px-2.5 py-2">
                                {comptabiliteOuverte
                                  ? "Rien n'est appliqué maintenant : la demande part au Centre de Support et sera exécutée après validation."
                                  : "Le changement prend effet dès la confirmation. Le motif reste consigné dans le dossier."}
                              </p>

                              <Button
                                onClick={confirmerChangementClasse}
                                disabled={changementEnCours || !classeCible || !motifChangement.trim()}
                                className="h-9 w-full justify-center bg-[#246990] hover:bg-[#1f5a7a] text-white text-sm"
                              >
                                {changementEnCours
                                  ? (comptabiliteOuverte ? 'Envoi en cours…' : 'Changement en cours…')
                                  : (comptabiliteOuverte ? 'Envoyer la demande' : 'Changer de classe')}
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Début des informations d'inscription */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Enregistré par</label>
                          <div className="font-medium bg-[#246990]/5 p-2 rounded-lg border border-[#246990]/10 text-gray-800">
                            {localEleve.inscripteur || 'Non renseigné'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date et heure</label>
                          <div className="font-medium bg-[#246990]/5 p-2 rounded-lg border border-[#246990]/10 text-gray-800">
                            {formatInscriptionDate(localEleve.date_inscription) || 'Non renseigné'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Type d'Admission</label>
                          <div className="font-medium bg-[#246990]/5 p-2 rounded-lg border border-[#246990]/10 text-gray-800">
                            {getAdmissionTypeLabel(localEleve.type_inscription)}
                          </div>
                        </div>
                        {/* Fin des informations d'inscription */}
                      </div>


                    </div>

                    {(isLoadingInterSiteTrace && hasInterSiteSignal) && (
                      <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-sky-100">
                        <div className="p-4 bg-sky-50 border-b border-sky-100 flex items-center gap-2">
                          <ArrowRightLeft size={17} className="text-[#246990]" />
                          <h3 className="font-semibold text-[#246990]">Traçabilité inter-site</h3>
                        </div>
                        <div className="p-5 text-sm text-gray-500">
                          Chargement de la traçabilité...
                        </div>
                      </div>
                    )}

                    {interSiteTrace?.is_transferred && (
                      <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-sky-100">
                        <div className="p-4 bg-sky-50 border-b border-sky-100">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <ArrowRightLeft size={17} className="text-[#246990]" />
                              <h3 className="font-semibold text-[#246990]">Traçabilité inter-site</h3>
                            </div>
                            <span className="inline-flex items-center gap-1 rounded-md bg-[#246990]/10 px-2 py-1 text-[11px] font-semibold text-[#246990]">
                              Transfert reçu
                            </span>
                          </div>
                          <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                            <div className="rounded-lg border border-sky-100 bg-white px-3 py-2">
                              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-gray-400">

                                Source
                              </div>
                              <div className="mt-1 text-sm font-bold text-gray-800 truncate">
                                {traceSite(interSiteTrace.source?.site)}
                              </div>
                            </div>
                            <ArrowRightLeft size={16} className="text-[#246990]" />
                            <div className="rounded-lg border border-emerald-100 bg-white px-3 py-2">
                              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-gray-400">

                                Site actuel
                              </div>
                              <div className="mt-1 text-sm font-bold text-gray-800 truncate">
                                {traceSite(interSiteTrace.cible?.site)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="p-5 space-y-4">
                          <div className="grid grid-cols-1 gap-3">
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                                <Clock3 size={13} />
                                Existence d'origine
                              </div>
                              <p className="mt-1 text-sm font-semibold text-gray-800">
                                {traceDate(interSiteTrace.source?.date_creation)}
                              </p>
                              <p className="mt-0.5 text-xs text-gray-500">
                                Enregistré par {traceValue(interSiteTrace.source?.created_by_nom)}
                              </p>
                            </div>

                            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-emerald-700">
                                <CheckCircle size={13} />
                                Arrivée sur ce site
                              </div>
                              <p className="mt-1 text-sm font-semibold text-gray-800">
                                {traceDate(interSiteTrace.cible?.date_arrivee)}
                              </p>
                              <p className="mt-0.5 text-xs text-emerald-700">
                                Classe cible : {traceValue(interSiteTrace.cible?.classe)}
                              </p>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <UserCheck size={14} className="mt-0.5 text-[#246990]" />
                              <div>
                                <p className="text-xs font-semibold text-gray-700">Demande créée</p>
                                <p className="text-xs text-gray-500">
                                  {traceValue(interSiteTrace.validation?.demande_creee_par)} - {traceDate(interSiteTrace.validation?.demande_creee_le)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <ShieldCheck size={14} className="mt-0.5 text-emerald-600" />
                              <div>
                                <p className="text-xs font-semibold text-gray-700">Transfert exécuté</p>
                                <p className="text-xs text-gray-500">
                                  {traceValue(interSiteTrace.validation?.execute_par)} - {traceDate(interSiteTrace.validation?.execute_le)}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-2">
                                <Archive size={16} className="mt-0.5 text-amber-600" />
                                <div>
                                  <p className="text-xs font-bold text-amber-900">Mémoire du transfert</p>
                                  <p className="mt-1 text-xs leading-relaxed text-amber-800">
                                    Une copie de sécurité des informations du site source a été conservée pour garder la preuve du parcours de l’élève avant son arrivée ici. Elle est consultable depuis ce dossier et reste aussi historisée côté Hub inter-site.
                                  </p>
                                </div>
                              </div>
                              <span className="shrink-0 rounded-md bg-white px-2 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-200">
                                {archiveTotal}
                              </span>
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-2">
                              {archiveDetails.filter(item => item.count > 0).map(item => (
                                <div key={item.label} className="rounded-lg bg-white px-3 py-2 ring-1 ring-amber-100">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="text-[11px] font-bold text-gray-700">{item.label}</p>
                                    <span className="text-xs font-bold text-amber-700">{item.count}</span>
                                  </div>
                                  <p className="mt-1 text-[11px] leading-relaxed text-gray-500">{item.description}</p>
                                </div>
                              ))}
                            </div>

                            {archiveDetails.every(item => item.count === 0) && (
                              <p className="mt-3 rounded-lg bg-white px-3 py-2 text-xs text-gray-500 ring-1 ring-amber-100">
                                Aucune donnée liée supplémentaire n’a été trouvée au moment du transfert. Seule la trace administrative du mouvement est conservée.
                              </p>
                            )}
                          </div>

                          {interSiteTrace.transfer_uuid && (
                            <div className="rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-500">
                              Réf. transfert : <span className="font-semibold text-gray-700">{interSiteTrace.transfer_uuid}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  </div>

                  {/* Colonne du milieu - Informations de l'élève */}
                  <div className="space-y-6">
                    <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-[#246990]/15">
                      <div className="p-4 bg-[#246990]/5 border-b border-[#246990]/10">
                        <h3 className="font-semibold text-[#246990]">Identité</h3>
                      </div>

                      <div className="p-5 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                          {isEditing ? (
                            <Input
                              name="nom"
                              value={localEleve.nom}
                              onChange={handleInputChange}
                              className="w-full"
                            />
                          ) : (
                            <div className="font-medium bg-[#246990]/5 p-2 rounded-lg border border-[#246990]/10 text-gray-800">
                              {localEleve.nom}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                          {isEditing ? (
                            <Input
                              name="prenom"
                              value={localEleve.prenom}
                              onChange={handleInputChange}
                              className="w-full"
                            />
                          ) : (
                            <div className="font-medium bg-[#246990]/5 p-2 rounded-lg border border-[#246990]/10 text-gray-800">
                              {localEleve.prenom}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                          {isEditing ? (
                            <Input
                              type="date"
                              name="dateNaissance"
                              value={localEleve.dateNaissance || ''}
                              onChange={handleInputChange}
                              className="w-full"
                            />
                          ) : (

                            <div className="font-medium bg-[#246990]/5 p-2 rounded-lg border border-[#246990]/10 text-gray-800">
                              {formatDate(localEleve.dateNaissance || localEleve.date_naissance)}
                            </div>

                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sexe</label>
                          {isEditing ? (
                            <select
                              name="sexe"
                              value={localEleve.sexe || ''}
                              onChange={handleInputChange}
                              className="w-full px-4 py-2.5 mt-1 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                            >
                              <option value="">Sélectionner</option>
                              <option value="M">Masculin</option>
                              <option value="F">Féminin</option>
                            </select>
                          ) : (
                            <div className="font-medium bg-[#246990]/5 p-2 rounded-lg border border-[#246990]/10 text-gray-800">
                              {localEleve.sexe === 'M' ? 'Masculin' : localEleve.sexe === 'F' ? 'Féminin' : 'Non renseigné'}
                            </div>
                          )}
                        </div>


                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Lieu de naissance</label>
                          {isEditing ? (
                            <Input
                              name="lieuNaissance"
                              value={localEleve.lieuNaissance === undefined ? localEleve.lieu_naissance || '' : localEleve.lieuNaissance}
                              onChange={(e) => {
                                setLocalEleve(prev => ({
                                  ...prev,
                                  lieuNaissance: e.target.value,
                                  lieu_naissance: undefined
                                }));
                              }}
                              className="w-full"
                            />
                          ) : (
                            <div className="font-medium bg-[#246990]/5 p-2 rounded-lg border border-[#246990]/10 text-gray-800">
                              {localEleve.lieuNaissance || localEleve.lieu_naissance || 'Non renseigné'}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nationalité</label>
                          {isEditing ? (
                            <Input
                              name="nationalite"
                              value={localEleve.nationalite || ''}
                              onChange={handleInputChange}
                              className="w-full"
                            />
                          ) : (
                            <div className="font-medium bg-[#246990]/5 p-2 rounded-lg border border-[#246990]/10 text-gray-800">
                              {localEleve.nationalite || 'Non renseigné'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Colonne de droite - Informations du parent/tuteur */}
                  <div className="space-y-6">
                    <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-teal-200/70">
                      <div className="p-4 bg-teal-50 border-b border-teal-100">
                        <h3 className="font-semibold text-teal-800">Parent / Tuteur</h3>
                      </div>

                      <div className="p-5 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nom et prénom</label>
                          {isEditing ? (
                            <Input
                              name="parent.nom_prenom"
                              value={localEleve.parent?.nom_prenom || ''}
                              onChange={handleInputChange}
                              className="w-full"
                            />
                          ) : (
                            <div className="font-medium bg-[#246990]/5 p-2 rounded-lg border border-[#246990]/10 text-gray-800">
                              {localEleve.parent?.nom_prenom || 'Non renseigné'}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                          {isEditing ? (
                            <Input
                              type="email"
                              name="parent.email"
                              value={localEleve.parent?.email || ''}
                              onChange={handleInputChange}
                              className="w-full"
                            />
                          ) : (
                            <div className="font-medium bg-[#246990]/5 p-2 rounded-lg border border-[#246990]/10 text-gray-800">
                              {localEleve.parent?.email || 'Non renseigné'}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                          {isEditing ? (
                            <Input
                              type="tel"
                              name="parent.telephone"
                              value={localEleve.parent?.telephone || ''}
                              onChange={handleInputChange}
                              className="w-full"
                            />
                          ) : (
                            <div className="font-medium bg-[#246990]/5 p-2 rounded-lg border border-[#246990]/10 text-gray-800">
                              {localEleve.parent?.telephone || 'Non renseigné'}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                          {isEditing ? (
                            <Textarea
                              name="parent.adresse"
                              value={localEleve.parent?.adresse || ''}
                              onChange={handleInputChange}
                              className="w-full"
                              rows={3}
                            />
                          ) : (
                            <div className="font-medium bg-[#246990]/5 p-2 rounded-lg border border-[#246990]/10 text-gray-800 min-h-[4rem]">
                              {localEleve.parent?.adresse || 'Non renseigné'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>






                </div>
              </Tabs.Content>

              <Tabs.Content value="documents">
                <div className="p-5 space-y-6 pb-10">
                  {/* Section Carte Étudiante */}
                  <ProtectedComponent module="ELEVES" sousModule="CARTE" action="GENERATE">
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl overflow-hidden shadow-sm border border-blue-200">
                      <div className="p-4 bg-[#246990] hover:bg-[#246990] border-b border-700">


                        <h3 className="font-semibold text-white flex items-center gap-2">
                          <IdCard size={18} />
                          Carte Étudiante
                        </h3>
                      </div>

                      <div className="p-5">
                        <div className="flex md:flex-row flex-col items-start gap-6">
                          {/* Aperçu de la carte */}
                          <div className="w-full md:w-1/2 rounded-lg overflow-hidden border border-gray-200 shadow-sm bg-white relative">
                            {photoPreview ? (
                              <div className="p-4 flex flex-col items-center">
                                <div className="w-full bg-[#246990] hover:bg-[#246990] text-white p-3 rounded-t-lg flex justify-between items-center">
                                  <div className="font-bold">CARTE ÉTUDIANTE</div>
                                  <div className="text-sm">Année Scolaire 2024-2025</div>
                                </div>

                                <div className="flex w-full p-4 gap-4">
                                  <div className="w-1/3">
                                    <img
                                      src={photoPreview}
                                      alt="Photo d'identité"
                                      className="w-full h-auto object-cover rounded-lg border-2 border-gray-200"
                                    />
                                  </div>

                                  <div className="w-2/3 space-y-2">
                                    <div>
                                      <div className="text-xs text-gray-500">Nom et prénom</div>
                                      <div className="font-semibold">{localEleve.nom} {localEleve.prenom}</div>
                                    </div>

                                    <div>
                                      <div className="text-xs text-gray-500">Matricule</div>
                                      <div className="font-semibold">{localEleve.matricule || "Non défini"}</div>
                                    </div>

                                    <div>
                                      <div className="text-xs text-gray-500">Classe</div>
                                      <div className="font-semibold">{localEleve.classe_nom}</div>
                                    </div>

                                    <div>
                                      <div className="text-xs text-gray-500">Date de naissance</div>
                                      <div className="font-semibold">
                                        {formatDate(localEleve.dateNaissance || localEleve.date_naissance)}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="w-full bg-gray-100 p-3 text-center text-xs text-gray-600 rounded-b-lg">
                                  Cette carte est personnelle et doit être présentée à l'entrée de l'établissement
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center py-6 px-4 text-center">
                                <Camera size={48} className="text-gray-300 mb-2" />
                                <p className="text-gray-500">Ajoutez une photo d'identité pour visualiser la carte</p>
                              </div>
                            )}
                          </div>

                          {/* Options de génération */}
                          <div className="w-full md:w-1/2 space-y-4">
                            <h4 className="font-medium text-gray-700">Génération de la carte</h4>
                            <p className="text-gray-600 text-sm">
                              La carte étudiante est un document officiel qui permet d'identifier l'élève et d'accéder aux services de l'établissement.
                            </p>

                            <div className="space-y-3">
                              <div className="flex items-center gap-2 text-sm">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${photoPreview ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                  {photoPreview ? <CheckCircle size={14} /> : <X size={14} />}
                                </div>
                                <span>Photo d'identité {photoPreview ? 'disponible' : 'manquante'}</span>
                              </div>

                              <div className="flex items-center gap-2 text-sm">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${localEleve.matricule ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                  {localEleve.matricule ? <CheckCircle size={14} /> : <X size={14} />}
                                </div>
                                <span>Matricule {localEleve.matricule ? 'défini' : 'non défini'}</span>
                              </div>
                            </div>

                            <div className="pt-2">

                              <Button
                                onClick={generateStudentCard}
                                disabled={!photoPreview || !localEleve.matricule}
                                className="flex items-center gap-2 bg-[#319190] hover:bg-[#63b5b4]/90 text-white text-base px-5 py-2 h-auto rounded-lg"
                              >
                                {isGeneratingCard ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                                ) : (
                                  <FileText size={18} />
                                )}
                                Générer la carte étudiante
                              </Button>




                              {(!photoPreview || !localEleve.matricule) && (
                                <p className="text-sm text-amber-600 mt-2">
                                  {!photoPreview && !localEleve.matricule
                                    ? "Une photo d'identité et un matricule sont nécessaires pour générer la carte."
                                    : !photoPreview
                                      ? "Une photo d'identité est nécessaire pour générer la carte."
                                      : "Un matricule est nécessaire pour générer la carte."}
                                </p>
                              )}
                            </div>

                          </div>

                        </div>
                      </div>

                    </div>
                  </ProtectedComponent>


                  {interSiteTrace?.is_transferred && (
                    <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-amber-100">
                      <div className="p-4 bg-amber-50 border-b border-amber-100">
                        <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                          <Archive size={18} className="text-amber-600" />
                          Documents archivés au transfert
                        </h3>
                        <p className="mt-1 text-xs text-amber-700">
                          Lors du transfert, le Hub a pris une copie de sécurité du dossier source. Les documents ci-dessous sont les pièces retrouvées dans cette archive.
                        </p>
                      </div>
                      <div className="p-5 space-y-3">
                        {interSiteTrace.archive?.documents?.length > 0 ? (
                          interSiteTrace.archive.documents.map((doc, index) => (
                            <div key={`${doc.table}-${doc.chemin || doc.nom_fichier}-${index}`} className="bg-amber-50/50 p-3 rounded-lg border border-amber-100 flex items-center justify-between gap-3">
                              <div className="flex items-center gap-3 min-w-0">
                                <FileText size={22} className="text-amber-600 shrink-0" />
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-800 truncate">{doc.nom_fichier}</p>
                                  <p className="text-xs text-gray-500">
                                    {traceValue(doc.type)} - {traceDate(doc.date_ajout)}
                                  </p>
                                </div>
                              </div>
                              {doc.chemin && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="flex items-center gap-2 text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                                  onClick={() => window.open(doc.chemin, '_blank')}
                                >
                                  <Download size={16} />
                                </Button>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="rounded-lg border border-amber-100 bg-amber-50/50 px-4 py-3">
                            <p className="text-sm font-semibold text-amber-900">Aucun document archivé</p>
                            <p className="mt-1 text-xs leading-relaxed text-amber-700">
                              Le transfert est bien tracé, mais aucun document administratif n’était attaché au dossier source au moment de la copie de sécurité.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Section Pièces d'identité */}
                  <ProtectedComponent module="ELEVES" sousModule="DOCUMENTS" action="VIEW">
                    <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-orange-100">
                      <div className="p-4 bg-orange-50 border-b border-orange-100 flex justify-between items-center">
                        <h3 className="font-semibold text-orange-800 flex items-center gap-2">
                          <IdCard size={18} className="text-orange-500" />
                          Pièces d'identité
                        </h3>
                        <ProtectedComponent module="ELEVES" sousModule="DOCUMENTS" action="UPLOAD">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.multiple = true;
                              input.accept = 'application/pdf,image/jpeg,image/png,image/jpg';
                              input.addEventListener('change', e => handleMultipleDocumentUpload(e, 'identite'));
                              input.click();
                            }}
                            className="flex items-center gap-2"
                          >
                            <Plus size={16} />
                            Ajouter
                          </Button>
                        </ProtectedComponent>

                      </div>

                      <div className="p-5">
                        {documents.identite && documents.identite.length > 0 ? (
                          <div className="space-y-3">
                            {documents.identite.map(doc => (
                              <div key={doc.id} className="bg-gray-50 p-3 rounded-lg border border-gray-200 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  {getDocumentIcon(doc)}
                                  <div>
                                    <p className="font-medium text-gray-800">{doc.nom_fichier}</p>
                                    <div className="flex items-center gap-4 text-sm text-gray-500">
                                      <span>{new Date(doc.date_ajout).toLocaleDateString()}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <ProtectedComponent module="ELEVES" sousModule="DOCUMENTS" action="DOWNLOAD">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                                      onClick={() => window.open(doc.chemin, '_blank')}
                                    >
                                      <Download size={16} />
                                    </Button>
                                  </ProtectedComponent>

                                  <ProtectedComponent module="ELEVES" sousModule="DOCUMENTS" action="DELETE">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="flex items-center gap-2 text-red-600 hover:text-red-800 hover:bg-red-50"
                                      onClick={() => handleDeleteDocument(doc.id)}
                                    >
                                      <Trash2 size={16} />
                                    </Button>
                                  </ProtectedComponent>

                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="bg-gray-50 py-10 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center">
                            <Paperclip size={32} className="text-gray-400 mb-3" />
                            <p className="text-gray-500 mb-2">Aucune pièce d'identité ajoutée</p>
                            <ProtectedComponent module="ELEVES" sousModule="DOCUMENTS" action="UPLOAD">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  const input = document.createElement('input');
                                  input.type = 'file';
                                  input.multiple = true;
                                  input.accept = 'application/pdf,image/jpeg,image/png,image/jpg';
                                  input.addEventListener('change', e => handleMultipleDocumentUpload(e, 'identite'));
                                  input.click();
                                }}
                                className="flex items-center gap-2 mt-2"
                              >
                                <Upload size={16} />
                                Ajouter des documents
                              </Button>
                            </ProtectedComponent>

                          </div>
                        )}
                      </div>
                    </div>
                  </ProtectedComponent>


                  {/* Section Diplômes */}
                  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-blue-100">
                    <div className="p-4 bg-blue-50 border-b border-blue-100 flex justify-between items-center">
                      <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                        <BookOpen size={18} className="text-blue-500" />
                        Diplômes
                      </h3>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.multiple = true;
                          input.accept = 'application/pdf,image/jpeg,image/png,image/jpg';
                          input.addEventListener('change', e => handleMultipleDocumentUpload(e, 'diplomes'));
                          input.click();
                        }}
                        className="flex items-center gap-2"
                      >
                        <Plus size={16} />
                        Ajouter
                      </Button>
                    </div>

                    <div className="p-5">
                      {documents.diplomes && documents.diplomes.length > 0 ? (
                        <div className="space-y-3">
                          {documents.diplomes.map(doc => (
                            <div key={doc.id} className="bg-gray-50 p-3 rounded-lg border border-gray-200 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {getDocumentIcon(doc)}
                                <div>
                                  <p className="font-medium text-gray-800">{doc.nom_fichier}</p>
                                  <div className="flex items-center gap-4 text-sm text-gray-500">
                                    <span>{new Date(doc.date_ajout).toLocaleDateString()}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                                  onClick={() => window.open(doc.chemin, '_blank')}
                                >
                                  <Download size={16} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="flex items-center gap-2 text-red-600 hover:text-red-800 hover:bg-red-50"
                                  onClick={() => handleDeleteDocument(doc.id)}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-gray-50 py-10 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center">
                          <BookOpen size={32} className="text-gray-400 mb-3" />
                          <p className="text-gray-500 mb-2">Aucun diplôme ajouté</p>
                          <Button
                            variant="outline"
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.multiple = true;
                              input.accept = 'application/pdf,image/jpeg,image/png,image/jpg';
                              input.addEventListener('change', e => handleMultipleDocumentUpload(e, 'diplomes'));
                              input.click();
                            }}
                            className="flex items-center gap-2 mt-2"
                          >
                            <Upload size={16} />
                            Ajouter des diplômes
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section Autres documents */}
                  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-purple-100">
                    <div className="p-4 bg-purple-50 border-b border-purple-100 flex justify-between items-center">
                      <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                        <File size={18} className="text-purple-500" />
                        Autres documents
                      </h3>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.multiple = true;
                          input.accept = 'application/pdf,image/jpeg,image/png,image/jpg';
                          input.addEventListener('change', e => handleMultipleDocumentUpload(e, 'autres'));
                          input.click();
                        }}
                        className="flex items-center gap-2"
                      >
                        <Plus size={16} />
                        Ajouter
                      </Button>
                    </div>

                    <div className="p-5">
                      {documents.autres && documents.autres.length > 0 ? (
                        <div className="space-y-3">
                          {documents.autres.map(doc => (
                            <div key={doc.id} className="bg-gray-50 p-3 rounded-lg border border-gray-200 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {getDocumentIcon(doc)}
                                <div>
                                  <p className="font-medium text-gray-800">{doc.nom_fichier}</p>
                                  <div className="flex items-center gap-4 text-sm text-gray-500">
                                    <span>{new Date(doc.date_ajout).toLocaleDateString()}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                                  onClick={() => window.open(doc.chemin, '_blank')}
                                >
                                  <Download size={16} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="flex items-center gap-2 text-red-600 hover:text-red-800 hover:bg-red-50"
                                  onClick={() => handleDeleteDocument(doc.id)}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-gray-50 py-10 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center">
                          <File size={32} className="text-gray-400 mb-3" />
                          <p className="text-gray-500 mb-2">Aucun document supplémentaire ajouté</p>
                          <Button
                            variant="outline"
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.multiple = true;
                              input.accept = 'application/pdf,image/jpeg,image/png,image/jpg';
                              input.addEventListener('change', e => handleMultipleDocumentUpload(e, 'autres'));
                              input.click();
                            }}
                            className="flex items-center gap-2 mt-2"
                          >
                            <Upload size={16} />
                            Ajouter des documents
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>


              </Tabs.Content>

              {busInstalle && (
                <Tabs.Content value="transport">
                  {activeTab === 'transport' && (
                    <div className="p-5">
                      <SectionTransportEleve eleveId={eleve?.id} />
                    </div>
                  )}
                </Tabs.Content>
              )}

              {espaceFamilleOuvert && (
                <Tabs.Content value="acces-famille">
                  {activeTab === 'acces-famille' && (
                    <AccesFamilleEleve eleveId={eleve?.id} onToast={onShowToast} />
                  )}
                </Tabs.Content>
              )}

            </div>

            {/* Pied de page fixe */}
            <div className="shrink-0 bg-white border-t border-gray-200 p-4 flex justify-between items-center z-10">
              {activeTab === 'details' && (
                <>
                  {isEditing ? (
                    <div className="flex space-x-3">
                      <Button
                        variant="outline"
                        onClick={handleCancel}
                        className="flex items-center gap-2 text-base px-5 py-2 h-auto"
                      >
                        <X size={18} />
                        Annuler
                      </Button>
                      <Button
                        onClick={handleSave}
                        className="flex items-center gap-2 bg-[#319190] hover:bg-[#63b5b4]/90 text-white text-base px-5 py-2 h-auto rounded-lg"
                      >
                        <Save size={18} />
                        Enregistrer
                      </Button>
                    </div>
                  ) : (

                    <ProtectedComponent
                      module="ELEVES"
                      sousModule="DETAILS"
                      action="EDIT"
                    >
                      <Button
                        onClick={() => {
                          // Récupérer la date actuelle
                          const currentDate = localEleve.dateNaissance || localEleve.date_naissance;

                          // Formater la date si nécessaire
                          if (currentDate) {
                            const formattedDate = formatDateForInput(currentDate);

                            // Mettre à jour l'état avec la date formatée
                            if (formattedDate && formattedDate !== localEleve.dateNaissance) {
                              setLocalEleve(prev => ({
                                ...prev,
                                dateNaissance: formattedDate
                              }));
                            }
                          }

                          setIsEditing(true);
                        }}
                        className="flex items-center gap-2 bg-[#c66d59] hover:bg-[#ae7467]/90 text-white text-base px-5 py-2 h-auto rounded-lg"
                      >
                        <Edit size={18} />
                        Modifier
                      </Button>
                    </ProtectedComponent>

                  )}
                </>
              )}

              {activeTab === 'documents' && (
                <div className="flex items-center text-emerald-600 bg-emerald-50 p-2 px-4 rounded-full">
                  <CheckCircle size={18} className="mr-2" />
                  <span className="text-sm font-medium">Les documents sont automatiquement sauvegardés</span>
                </div>
              )}

              <Button
                variant="outline"
                onClick={onClose}
                className="text-base px-5 py-2 h-auto"
              >
                Fermer
              </Button>
            </div>
          </Tabs.Root>
        </Dialog.Content>
      </Dialog.Portal>

      <StudentCardModal
        isOpen={isCardModalOpen}
        onClose={() => setIsCardModalOpen(false)}
        student={localEleve}
      />

    </Dialog.Root>


  );
};

export default DetailModal;
